import React, { useState, useEffect, useRef } from 'react';
import {
  Table,
  Button,
  Modal,
  Form,
  Input,
  message,
  Space,
  Tag,
  Alert,
} from 'antd';
import { Helmet } from 'react-helmet';
import { SearchOutlined } from '@ant-design/icons';
import api from '../../services/api';

const UserManagement = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [visible, setVisible] = useState(false);
  const [form] = Form.useForm();
  const [editingId, setEditingId] = useState(null);
  const searchInput = useRef(null);
  const [searchText, setSearchText] = useState('');
  const [searchedColumn, setSearchedColumn] = useState('');

  // Success and error messages state to show Alerts above the table
  const [successMsg, setSuccessMsg] = useState('');
  const [errorMsg, setErrorMsg] = useState('');

  // Function to clear messages on new action
  const clearMessages = () => {
    setSuccessMsg('');
    setErrorMsg('');
  };

  const handleSearch = (selectedKeys, confirm, dataIndex) => {
    confirm();
    setSearchText(selectedKeys[0]);
    setSearchedColumn(dataIndex);
  };

  const handleReset = (clearFilters) => {
    clearFilters();
    setSearchText('');
  };

  // Search filter for columns
  const getColumnSearchProps = (dataIndex) => ({
    filterDropdown: ({
      setSelectedKeys,
      selectedKeys,
      confirm,
      clearFilters,
    }) => (
      <div style={{ padding: 8 }}>
        <Input
          ref={searchInput}
          placeholder={`Search ${dataIndex}`}
          value={selectedKeys[0]}
          onChange={(e) =>
            setSelectedKeys(e.target.value ? [e.target.value] : [])
          }
          onPressEnter={() => handleSearch(selectedKeys, confirm, dataIndex)}
          style={{ marginBottom: 8, display: 'block' }}
          autoFocus
        />
        <Space>
          <Button
            type="primary"
            onClick={() => handleSearch(selectedKeys, confirm, dataIndex)}
            icon={<SearchOutlined />}
            size="small"
            style={{ width: 90 }}
          >
            Search
          </Button>
          <Button
            onClick={() => handleReset(clearFilters)}
            size="small"
            style={{ width: 90 }}
          >
            Reset
          </Button>
        </Space>
      </div>
    ),
    filterIcon: (filtered) => (
      <SearchOutlined style={{ color: filtered ? '#1890ff' : undefined }} />
    ),
    onFilter: (value, record) =>
      record[dataIndex]
        ? record[dataIndex].toString().toLowerCase().includes(value.toLowerCase())
        : '',
    onFilterDropdownVisibleChange: (visible) => {
      if (visible) {
        setTimeout(() => searchInput.current?.select(), 100);
      }
    },
    render: (text) => {
      if (searchedColumn === dataIndex && searchText) {
        const regex = new RegExp(searchText, 'gi');
        const parts = String(text).split(regex);
        return (
          <>
            {parts.map((part, index) =>
              index > 0 ? (
                <span key={index} style={{ backgroundColor: '#ffc069' }}>
                  {searchText}
                </span>
              ) : (
                part
              )
            )}
          </>
        );
      }
      return text;
    },
  });

  const columns = [
{
  title: 'S.No.',
  key: 'sno',
  width: 80,
  render: (_text, _record, index) => index + 1,
},
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      ...getColumnSearchProps('name'),
    },
    {
      title: 'Email',
      dataIndex: 'email',
      key: 'email',
      ...getColumnSearchProps('email'),
    },
    {
      title: 'Mobile No.',
      dataIndex: 'contact_no',
      key: 'contact_no',
      ...getColumnSearchProps('contact_no'),
    },
    {
      title: 'Type',
      dataIndex: 'user_type',
      key: 'user_type',
      render: (user_type) => (
        <Tag color={user_type === 'admin' ? 'red' : 'blue'}>{user_type}</Tag>
      ),
      ...getColumnSearchProps('user_type'),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status) => (
        <Tag
          color={
            status === 'approved' ? 'green' : status === 'pending' ? 'orange' : 'red'
          }
        >
          {status}
        </Tag>
      ),
      ...getColumnSearchProps('status'),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space size="middle">
          <Button onClick={() => handleEdit(record)}>Edit</Button>
          <Button danger onClick={() => handleDelete(record.user_id)}>
            Delete
          </Button>
          {record.status !== 'approved' && (
            <Button type="primary" onClick={() => handleApprove(record.user_id)}>
              Approve
            </Button>
          )}
        </Space>
      ),
    },
  ];

  useEffect(() => {
    fetchUsers();
  }, []);

    useEffect(() => {
  if (successMsg) {
    const timer = setTimeout(() => setSuccessMsg(''), 5000);
    return () => clearTimeout(timer);
  }
}, [successMsg]);
  const fetchUsers = async () => {
    setLoading(true);

    try {
      const response = await api.get('/users/pending');
      if (response.data.success) {
        setUsers(response.data.users);
      } else {
        setErrorMsg('Failed to fetch users');
      }
    } catch (error) {
      setErrorMsg('Failed to fetch users'); 
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    try {
      clearMessages();
      const values = await form.validateFields();
      setLoading(true);

      // Map form field `type` to `user_type`
      const payload = {
        name: values.name,
        email: values.email,
        user_type: values.user_type || values.type,
        contact_no: values.contact_no || null,
        username: values.username || null,
        ...(values.password ? { password: values.password } : {}),
      };

      let response;
      if (editingId) {
        response = await api.put(`/users/${editingId}`, payload);
      } else {
        response = await api.post('/users', payload);
      }

      if (response.data.success) {
        setSuccessMsg(response.data.message);
        setVisible(false);
        form.resetFields();
        fetchUsers();
      } else {
        setErrorMsg(response.data.message || 'Operation failed');
      }
    } catch (error) {
      setErrorMsg(error.response?.data?.message || 'Operation failed');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (user) => {
    clearMessages();
    form.setFieldsValue({
      ...user,
      password: '',
      user_type: user.user_type,
    });
    setEditingId(user.user_id);
    setVisible(true);
  };

  const handleDelete = (user_id) => {
    clearMessages();
    Modal.confirm({
      title: 'Are you sure you want to delete this user?',
      okText: 'Yes',
      okType: 'danger',
      cancelText: 'No',
      onOk: async () => {
        setLoading(true);
        try {
          const response = await api.delete(`/users/${user_id}`);
          if (response.data && response.data.success) {
            setSuccessMsg(response.data.message);
            fetchUsers(); // Refresh the list after deletion
          } else {
            setErrorMsg(response.data.message || 'Failed to delete user');
          }
        } catch (error) {
          setErrorMsg(error.response?.data?.message || 'Failed to delete user');
        } finally {
          setLoading(false);
        }
      },
    });
  };

  const handleApprove = async (user_id) => {
    clearMessages();
    try {
      setLoading(true);
      const response = await api.put(`/users/${user_id}/approve`);
      if (response.data.success) {
        setSuccessMsg(response.data.message);
        fetchUsers();
      } else {
        setErrorMsg(response.data.message || 'Failed to approve user');
      }
    } catch (error) {
      setErrorMsg(error.response?.data?.message || 'Failed to approve user');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>User Management</title>
      </Helmet>
      <div className="user-management" style={{ padding: '20px' }}>
     

        {/* Success and Error Alerts */}
        {successMsg && (
          <Alert
            style={{ marginBottom: 16 }}
            message={successMsg}
            type="success"
            closable
            onClose={() => setSuccessMsg('')}
          />
        )}
        {errorMsg && (
          <Alert
            style={{ marginBottom: 16 }}
            message={errorMsg}
            type="error"
            closable
            onClose={() => setErrorMsg('')}
          />
        )}

        <Table
          columns={columns}
          dataSource={users}
          rowKey="user_id"
          loading={loading}
          pagination={{ pageSize: 10 }}
          scroll={{ x: 'max-content' }}
        />

        <Modal
          title={editingId ? 'Edit User' : 'Create User'}
          visible={visible}
          onOk={handleSubmit}
          onCancel={() => setVisible(false)}
          confirmLoading={loading}
          destroyOnClose
        >
          <Form form={form} layout="vertical">
            <Form.Item
              name="name"
              label="Name"
              rules={[{ required: true, message: 'Please input the name!' }]}
            >
              <Input />
            </Form.Item>
            <Form.Item
              name="email"
              label="Email"
              rules={[
                { required: true, message: 'Please input the email!' },
                { type: 'email', message: 'Please input a valid email!' },
              ]}
            >
              <Input />
            </Form.Item>
            {!editingId && (
              <Form.Item
                name="password"
                label="Password"
                rules={[{ required: true, message: 'Please input the password!' }]}
              >
                <Input.Password />
              </Form.Item>
            )}
            {editingId && (
              <Form.Item
                name="password"
                label="Password"
                tooltip="Leave blank if you don't want to change the password"
                rules={[]}
              >
                <Input.Password />
              </Form.Item>
            )}
            <Form.Item
              name="user_type"
              label="User Type"
              rules={[{ required: true, message: 'Please input the user type!' }]}
            >
              <Input placeholder="e.g. admin or user" />
            </Form.Item>
            <Form.Item name="contact_no" label="Contact No.">
              <Input />
            </Form.Item>
            <Form.Item name="username" label="Username">
              <Input />
            </Form.Item>
          </Form>
        </Modal>
      </div>
    </>
  );
};

export default UserManagement;
